import type Organization from '@this/domain/organization/organization2';
import type { TransitSearchApi } from '@this/domain/organization/organization2';
import type { AvailableRepository } from '@this/domain/available_repository';
import type SearchQuery from './search_query';
import SearchResult from './search_result';
import type { PriceResultArgs } from '../api/domestic_air/prices_api';
import type { TransitPriceJson } from './select_repository';
import TransitList from './transit/transit_list';
import type { PriceFetchTarget } from './transit/price_fetch_target';
import { resolvePriceFetchTarget } from './transit/price_fetch_target';

type PriceUpdateArgs = Pick<
  PriceResultArgs,
  'flightNumber' | 'changeable' | 'seatType' | 'price' | 'ticketType' | 'seatCount' | 'connectingGroup'
>;

export type SelectType = 'separate' | 'airPackage' | 'jrPackage';

export type PackageProvider = 'wbf' | 'rakuten';

export type Airline = 'ANA' | 'JAL';

export type Tab =
  | 'none' // 何も選択しない。
  | 'outword'
  | 'homeword'
  | 'hotel'
  | 'packageHotel'
  | 'packageOutword'
  | 'packageHomeword'
  | number;

class SelectStore {
  public result: SearchResult;

  public query: SearchQuery;

  public currentTab: Tab;

  public packageLoading: { ANA: boolean; JAL: boolean };

  public jrPackageLoading: boolean;

  public packageErrors: { ANA: string[] | null | undefined; JAL: string[] | null | undefined };

  public jrPackageErrors: string[] | null | undefined;

  public showPackage: boolean;

  public signedIn: boolean;

  public flashMsgEnd: boolean;

  public needReplaceSegments: boolean;

  public availableRepos: AvailableRepository[];

  // TODO: UserJson定義
  public user: {
    email: string;
    organization?: {
      arrangement_request_setting: {
        enable: boolean;
        fee?: number | null;
      };
      reserve_request_mail_notification_type: Organization['reserveRequestMailNotificationType'];
      // userの渡され方が動線によって異なり、organizationのプロパティ名が変わる
      // - 検索から始める（/select）: APIのレスポンスをそのまま渡すため transit_search_api
      // - 申請から始める（/trips/new、旅程の編集）: Organization クラスへ変換して渡すため transitSearchApi
      transit_search_api?: TransitSearchApi;
      transitSearchApi?: TransitSearchApi;
    };
    domestic_air_seat_upgrade: boolean;
    show_kabuyu_and_normal_fare_simultaneously: boolean;
  } | null;

  public appliedFlightQuerySetting: { carrier: boolean; flight_type: boolean } = {
    carrier: false,
    flight_type: false
  };

  // 価格取得中の航路セット（航路キー: "HND-FUK-2026-02-14"）
  public fetchingPriceRoutes: Set<string> = new Set();

  // 再取得した一覧に含まれていなかった便のtransit ID。売り切れ・欠航・取扱終了のいずれも予約できないため選択不可にする
  public unavailableTransitIds: Set<string> = new Set();

  // 経路検索の種別を返す。
  // organizationのプロパティ名が動線によって異なるため、両方のキーを見る。
  // - 検索から始める（/select）: transit_search_api（APIのレスポンスをそのまま保持）
  // - 申請から始める（/trips/new、旅程の編集）: transitSearchApi（Organization クラスへ変換して保持）
  transitSearchApi(): TransitSearchApi {
    const org = this.user?.organization;
    return org?.transit_search_api ?? org?.transitSearchApi ?? 'legacy';
  }

  constructor(args: { query: SearchQuery }, user: any, availableRepos: AvailableRepository[]) {
    this.result = new SearchResult(args);
    this.query = args.query;
    this.currentTab = 'none';
    this.packageErrors = { ANA: null, JAL: null };
    this.signedIn = false;
    this.user = user;
    this.showPackage = !this.query.disabledPackageSearch;
    this.packageLoading = { ANA: this.showPackage, JAL: this.showPackage };
    this.jrPackageLoading = this.showPackage;
    this.flashMsgEnd = false;
    this.needReplaceSegments = false;
    this.availableRepos = availableRepos ?? [];
  }

  handleClickTab = (tab: Tab) => {
    this.currentTab = tab;
    app.render();
  };

  setTab = (tab: Tab) => () => {
    this.currentTab = tab;
  };

  pushHistory = () => {
    window.history.replaceState(null, '', `/select${this.query.getQueryString()}`);
  };

  setBasicError = () => {
    app.render();
  };

  searchProgress = (serviceId: number) => {
    let Percentage = 0;
    if (utils.isMynavi(serviceId)) {
      Percentage += this.packageLoading.ANA ? 0 : 50;
      Percentage += this.packageLoading.JAL ? 0 : 50;
    } else {
      Percentage += this.packageLoading.ANA ? 0 : 33.3;
      Percentage += this.packageLoading.JAL ? 0 : 33.3;
      Percentage += this.jrPackageLoading ? 0 : 33.3;
    }
    return Math.round(Percentage);
  };

  searchProgressAna() {
    return this.packageLoading.ANA;
  }

  searchProgressJal() {
    return this.packageLoading.JAL;
  }

  searchProgressJr() {
    return this.jrPackageLoading;
  }

  searchProgressAll(serviceId: number) {
    let flg = true;
    if (utils.isMynavi(serviceId)) {
      flg = this.packageLoading.ANA || this.jrPackageLoading;
    } else {
      flg = this.packageLoading.ANA || this.jrPackageLoading || this.jrPackageLoading;
    }
    if (!flg) {
      setTimeout(() => {
        this.flashMsgEnd = true;
      }, 3000);
    }
    return flg;
  }

  searchPackageResultText(serviceId: number) {
    let findFlg = false;
    if (this.result.getAirPackageTrip('ANA').packageFinalPrice() !== 0) {
      findFlg = true;
    }
    if (this.result.getAirPackageTrip('JAL').packageFinalPrice() !== 0) {
      findFlg = true;
    }
    if (!utils.isMynavi(serviceId) && this.result.getJrPackageTrip().packageFinalPrice() !== 0) {
      findFlg = true;
    }

    if (findFlg) {
      return 'パッケージが見つかりました';
    }
    return 'パッケージが見つかりませんでした';
  }

  isEndFlashMsg() {
    return this.flashMsgEnd;
  }

  cheapestPricePackage(serviceId: number) {
    const priceMap = [{ key: 'INDI', price: this.result.getReservingTrip().separateFinalPrice() }];
    if (this.result.getAirPackageTrip('ANA').packageFinalPrice() !== 0) {
      priceMap.push({ key: 'ANA', price: this.result.getAirPackageTripCheapest('ANA').packageFinalPrice() });
    }
    if (this.result.getAirPackageTrip('JAL').packageFinalPrice() !== 0) {
      priceMap.push({ key: 'JAL', price: this.result.getAirPackageTripCheapest('JAL').packageFinalPrice() });
    }
    if (!utils.isMynavi(serviceId) && this.result.getJrPackageTrip().packageFinalPrice() !== 0) {
      priceMap.push({ key: 'JR', price: this.result.getJrPackageTripCheapest().packageFinalPrice() });
    }
    priceMap.sort((a, b) => {
      if (a.price < b.price) return -1;
      if (a.price > b.price) return 1;
      return 0;
    });
    return priceMap[0].key;
  }

  isTravelport() {
    return this.availableRepos.some(
      repo => repo.category === 'foreign_flight' && repo.repository === 'travelport'
    );
  }

  // TravelportもAmadeusも6区間毎に検索する
  maxSegments() {
    return 6;
  }

  get isCurrentAirlinePackageLoading() {
    return this.packageLoading[this.result.airline];
  }

  collectTransitIds = (
    fromAirport: string,
    toAirport: string,
    targetDate: string,
    fetchTarget: PriceFetchTarget
  ): string[] => {
    const ids: string[] = [];
    this.result.items.forEach(item => {
      if (!item.elementList || !(item.elementList instanceof TransitList)) return;
      const transitList = item.elementList as TransitList;
      transitList.list.forEach(transit => {
        if (!transit.airExists()) return;
        const airSegments = transit.segments.filter(s => s.isAir());
        if (airSegments.length === 0) return;
        const firstLeg = airSegments[0].firstLeg();
        const lastLeg = airSegments[airSegments.length - 1].lastLeg();
        if (!firstLeg || !lastLeg) return;
        const legDateStr = firstLeg.from?.time_full;
        const legDate = typeof legDateStr === 'string' ? legDateStr.split('T')[0] : undefined;
        if (firstLeg.from?.code === fromAirport && lastLeg.to?.code === toAirport && legDate === targetDate) {
          // 同じ航路にANA由来便とskyticket/travelko由来便が混在するため、取得元が一致する便だけを対象にする
          const sources = airSegments.flatMap(s => s.sources ?? []);
          if (resolvePriceFetchTarget(sources) === fetchTarget) {
            ids.push(transit.id);
          }
        }
      });
    });
    return ids;
  };

  updateAllTransitPrices = (
    fromAirport: string,
    toAirport: string,
    targetDate: string,
    prices: PriceUpdateArgs[],
    fetchTarget: PriceFetchTarget
  ) => {
    this.result.items.forEach((item, itemIdx) => {
      if (!item.elementList || !(item.elementList instanceof TransitList)) return;

      const transitList = item.elementList as TransitList;
      transitList.list.forEach((transit, transitIdx) => {
        // 航空便のみ対象
        if (!transit.airExists()) return;

        // 乗り継ぎ便（Segment 2つ）の場合、全体のfrom/toで比較する
        const airSegments = transit.segments.filter(s => s.isAir());
        if (airSegments.length === 0) return;

        const firstLeg = airSegments[0].firstLeg();
        const lastLeg = airSegments[airSegments.length - 1].lastLeg();
        if (!firstLeg || !lastLeg) return;

        // 航路と日付が一致するか確認
        const legFromAirport = firstLeg.from?.code;
        const legToAirport = lastLeg.to?.code;
        const legDateStr = firstLeg.from?.time_full;
        const legDate = typeof legDateStr === 'string' ? legDateStr.split('T')[0] : undefined;

        if (legFromAirport !== fromAirport || legToAirport !== toAirport || legDate !== targetDate) {
          return;
        }
        // ANA由来便とskyticket/travelko由来便は便名が同じ形式（ANA001など）になるため、
        // 便名だけで突合すると別の取得元の運賃で上書きしてしまう
        const segmentSources = airSegments.flatMap(s => s.sources ?? []);
        if (resolvePriceFetchTarget(segmentSources) !== fetchTarget) {
          return;
        }
        if (transit.prices && transit.prices.length > 0) {
          const connectingGroup = transit.prices[0].connecting_group;
          const flightNumber =
            transit.prices[0].flight_number ||
            `${firstLeg.airline_name || ''}${firstLeg.name || ''}`.trim() ||
            firstLeg.name;
          const flightPrices = connectingGroup
            ? prices.filter(rp => rp.connectingGroup === connectingGroup)
            : prices.filter(rp => rp.flightNumber === flightNumber && !rp.connectingGroup);

          // 取得できた一覧にこの便が無い場合は予約できないため、選択不可とする
          if (flightPrices.length === 0) {
            this.unavailableTransitIds.add(transit.id);
          } else {
            this.unavailableTransitIds.delete(transit.id);
          }

          if (flightPrices.length > 0) {
            const currentIndex = transitList.domesticAirPriceIndex || 0;
            const currentPrice = transit.prices[currentIndex];

            const newPrices = flightPrices.map(rp => ({
              flight_number: rp.flightNumber,
              price: rp.price,
              seat_type: rp.seatType,
              ticket_category: rp.changeable ? 'changeable' : 'unchangeable',
              ticket_type: rp.ticketType,
              seat_count: rp.seatCount ?? 'unknown',
              price_expired: false,
              ...(rp.connectingGroup ? { connecting_group: rp.connectingGroup } : {})
            }));
            transit.prices = newPrices as TransitPriceJson[];
            (transit as any).pricesOriginal = newPrices;
            if ((transit as any).raw) {
              (transit as any).raw.prices = newPrices;
            }

            if (transitList.currentId === transit.id) {
              const newIndex = currentPrice
                ? newPrices.findIndex(
                    p =>
                      p.seat_type === currentPrice.seat_type &&
                      p.ticket_category === currentPrice.ticket_category &&
                      p.ticket_type === currentPrice.ticket_type
                  )
                : -1;
              // 選択中の運賃が再取得後に無くなった場合、古いインデックスが範囲外や別の運賃を指すため先頭へ戻す
              transitList.domesticAirPriceIndex = newIndex >= 0 ? newIndex : 0;
            }
          }
        }
      });
    });

    app.render();
  };
}

export default SelectStore;
